import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { addBlogAdmin, removeBlogAdmin } from '../store/blogSlice';
import { selectCurrentBlog } from '../store/selectors';
import type { Blog } from '../types';
import type { BlogPermissions, BlogPermissionUser } from '../types';
import { blogAPI } from '../api/blogApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Crown, UserPlus, UserMinus, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { TransferOwnershipDialog } from './TransferOwnershipDialog';
import { useNavigate } from 'react-router-dom';

interface PermissionManagerProps {
  blog: Blog;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const PermissionManager = ({ blog: initialBlog, open, onOpenChange }: PermissionManagerProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [removingAdmin, setRemovingAdmin] = useState<BlogPermissionUser | null>(null);
  const [permissions, setPermissions] = useState<BlogPermissions | null>(null);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get fresh blog data from Redux store to ensure real-time updates
  const currentBlog = useAppSelector(selectCurrentBlog);
  const blog = currentBlog || initialBlog;

  const admins = permissions?.admins || [];

  useEffect(() => {
    if (!open) return;
    setPermissionsLoading(true);
    blogAPI.admin.getBlogPermissions(blog.id)
      .then((response) => setPermissions(response.data))
      .catch(() => toast.error('Không thể tải danh sách phân quyền'))
      .finally(() => setPermissionsLoading(false));
  }, [open, blog.id]);

  const handleAddAdmin = async () => {
    const email = adminEmail.trim().toLowerCase();

    if (!email) {
      toast.error('Vui lòng nhập email');
      return;
    }

    if (!/^\S+@\S+\.\S+$/.test(email)) {
      toast.error('Email không hợp lệ');
      return;
    }

    if (permissions?.owner.email.toLowerCase() === email || admins.some((admin) => admin.email.toLowerCase() === email)) {
      toast.error('Email này đã có quyền trên blog');
      return;
    }

    setIsSubmitting(true);
    try {
      await dispatch(addBlogAdmin({ blogId: blog.id, email })).unwrap();
      toast.success(`Đã thêm ${email} làm admin`);
      setShowAddDialog(false);
      setAdminEmail('');
      const refreshed = await blogAPI.admin.getBlogPermissions(blog.id);
      setPermissions(refreshed.data);
    } catch (error: any) {
      toast.error(error?.message || 'Không thể thêm admin');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveAdmin = async () => {
    if (!removingAdmin) return;

    setIsSubmitting(true);
    try {
      await dispatch(removeBlogAdmin({ blogId: blog.id, email: removingAdmin.email })).unwrap();
      toast.success(`Đã xóa ${removingAdmin.email} khỏi danh sách admin`);
      setShowRemoveDialog(false);
      setRemovingAdmin(null);
    } catch (error: any) {
      toast.error(error?.message || 'Không thể xóa admin');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openRemoveDialog = (admin: BlogPermissionUser) => {
    setRemovingAdmin(admin);
    setShowRemoveDialog(true);
  };

  return (
    <>
      {/* Main Permission Dialog */}
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="text-xl">Quản lý Phân quyền</DialogTitle>
            <DialogDescription>
              Quản lý người sở hữu và quản trị viên của blog "{blog.title}"
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[calc(80vh-180px)] overflow-y-auto pr-4">
            <div className="space-y-6 py-4">
              {/* Owner Section */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <Crown className="h-4 w-4 text-yellow-600" />
                  Người sở hữu
                </h3>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-yellow-900">
                    {permissionsLoading ? 'Đang tải...' : permissions?.owner.email || 'Không xác định'}
                  </p>
                  <p className="text-xs text-yellow-700 mt-1">
                    Có toàn quyền quản lý blog, thêm/xóa admin, và xóa blog
                  </p>
                </div>
              </div>

              {/* Admins Section */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-700">
                    Quản trị viên ({admins.length})
                  </h3>
                  <Button
                    size="sm"
                    onClick={() => setShowAddDialog(true)}
                    className="h-9"
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Thêm Admin
                  </Button>
                </div>

                {admins.length > 0 ? (
                  <div className="space-y-2">
                    {admins.map((admin) => (
                      <div
                        key={admin.id}
                        className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg p-3"
                      >
                        <p className="text-sm text-gray-900">{admin.email}</p>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openRemoveDialog(admin)}
                          className="h-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <UserMinus className="h-4 w-4 mr-1" />
                          Xóa
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                    <p className="text-sm text-gray-500">Chưa có admin nào</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Click "Thêm Admin" để thêm người quản lý
                    </p>
                  </div>
                )}
              </div>

              {/* Transfer Ownership Section */}
              <div className="pt-2 border-t border-gray-200">
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <RefreshCw className="h-5 w-5 text-orange-600" />
                      <h4 className="text-sm font-semibold text-orange-900">
                        Chuyển quyền sở hữu
                      </h4>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => setShowTransferDialog(true)}
                      className="bg-orange-600 hover:bg-orange-700 whitespace-nowrap"
                    >
                      Chuyển quyền
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Đóng
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Admin Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Thêm Quản Trị Viên</DialogTitle>
            <DialogDescription>
              Nhập email tài khoản Học Nhé của người bạn muốn thêm làm admin.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Email</label>
              <Input
                type="email"
                placeholder="user@example.com"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !isSubmitting) {
                    handleAddAdmin();
                  }
                }}
                disabled={isSubmitting}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAddDialog(false)}
              disabled={isSubmitting}
            >
              Hủy
            </Button>
            <Button onClick={handleAddAdmin} disabled={isSubmitting}>
              {isSubmitting ? 'Đang thêm...' : 'Thêm Admin'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Admin Dialog */}
      <AlertDialog open={showRemoveDialog} onOpenChange={setShowRemoveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Xác nhận xóa admin?</AlertDialogTitle>
            <AlertDialogDescription>
              Bạn có chắc chắn muốn xóa <strong>{removingAdmin?.email}</strong> khỏi danh sách admin?
              Người này sẽ mất quyền quản lý blog.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSubmitting}>Hủy</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveAdmin}
              disabled={isSubmitting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isSubmitting ? 'Đang xóa...' : 'Xóa Admin'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Transfer Ownership Dialog */}
      <TransferOwnershipDialog
        blog={blog}
        ownerEmail={permissions?.owner.email}
        open={showTransferDialog}
        onOpenChange={setShowTransferDialog}
        onSuccess={() => {
          // After successful transfer, navigate to profile since user is no longer owner
          navigate('/profile');
        }}
      />
    </>
  );
};
